import { useState } from "react";
import { CTA_OPTIONS } from "../data/mock";
import { Button } from "./ui/button";
import { ArrowRight, Send, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import { PrivacyNotice } from "./PrivacyNotice";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CTABlock = () => {
  const [selectedPath, setSelectedPath] = useState(null);
  const [formData, setFormData] = useState({ firstName: "", lastName: "", email: "", company: "", website: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error("Please fill in your first name, last name, and email.");
      return;
    }
    if (!privacyConsent) {
      toast.error("Please agree to the privacy policy to proceed.");
      return;
    }

    setLoading(true);
    try {
      const msgParts = [];
      if (formData.company) msgParts.push(`Company: ${formData.company}`);
      if (formData.website) msgParts.push(`Website: ${formData.website}`);
      if (formData.message) msgParts.push(formData.message);
      await axios.post(`${API}/enquiries`, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        message: msgParts.join("\n") || null,
        selected_path: selectedPath,
      });
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch shortly.");
      setTimeout(() => {
        setSubmitted(false);
        setSelectedPath(null);
        setFormData({ firstName: "", lastName: "", email: "", company: "", website: "", message: "" });
      }, 5000);
    } catch (err) {
      console.error("Enquiry submission error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">
            Ready to Modernise{" "}
            <span className="text-[#308795]">Your Operations?</span>
          </h2>
          <p className="text-[#6B6567] text-lg">
            Tell us about your needs. We'll connect you with the right solution.
          </p>
        </div>

        {/* Path Options */}
        {!selectedPath && !submitted && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {CTA_OPTIONS.map((opt, i) => {
              const Icon = opt.icon;
              return (
                <button
                  key={i}
                  onClick={() => setSelectedPath(opt.label)}
                  className="group flex items-center gap-4 bg-[#FAFAF7] border border-[#E0D1BB]/40 rounded-xl p-5 text-left transition-all duration-300 hover:shadow-md hover:border-[#308795]/30 hover:-translate-y-0.5"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#308795]/[0.08] flex items-center justify-center shrink-0 group-hover:bg-[#308795]/[0.12] transition-colors">
                    <Icon className="w-6 h-6 text-[#308795]" />
                  </div>
                  <div className="flex-1">
                    <span className="text-base font-semibold text-[#2C2A2B] block">
                      {opt.label}
                    </span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#D5CFC9] group-hover:text-[#308795] transition-all duration-300 group-hover:translate-x-1" />
                </button>
              );
            })}
          </div>
        )}

        {/* Contact Form */}
        {selectedPath && !submitted && (
          <div className="max-w-lg mx-auto bg-[#FAFAF7] rounded-2xl border border-[#E0D1BB]/30 p-8 animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <div>
                <span className="text-xs font-bold text-[#308795] uppercase tracking-wider">
                  Selected Path
                </span>
                <p className="text-lg font-bold text-[#2C2A2B]">{selectedPath}</p>
              </div>
              <button
                onClick={() => setSelectedPath(null)}
                className="p-2 rounded-lg hover:bg-white transition-colors text-[#8A8385]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#494546] mb-1.5">First Name</label>
                  <input type="text" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-[#E0D1BB]/50 bg-white text-[#2C2A2B] placeholder-[#C5BFB9] text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="First name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#494546] mb-1.5">Last Name</label>
                  <input type="text" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-[#E0D1BB]/50 bg-white text-[#2C2A2B] placeholder-[#C5BFB9] text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="Last name" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#494546] mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[#E0D1BB]/50 bg-white text-[#2C2A2B] placeholder-[#C5BFB9] text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all"
                  placeholder="your@email.com"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#494546] mb-1.5">Name of Company</label>
                  <input type="text" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-[#E0D1BB]/50 bg-white text-[#2C2A2B] placeholder-[#C5BFB9] text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="Company name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#494546] mb-1.5">Website</label>
                  <input type="url" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-[#E0D1BB]/50 bg-white text-[#2C2A2B] placeholder-[#C5BFB9] text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="https://www.example.com" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#494546] mb-1.5">
                  Message (Optional)
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-[#E0D1BB]/50 bg-white text-[#2C2A2B] placeholder-[#C5BFB9] text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all resize-none"
                  placeholder="Tell us about your needs..."
                />
              </div>
              <PrivacyNotice checked={privacyConsent} onChange={setPrivacyConsent} />
              <Button
                type="submit"
                disabled={loading || !privacyConsent}
                className="w-full bg-[#308795] hover:bg-[#267580] text-white font-semibold py-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#308795]/20 text-base disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Enquiry
                    <Send className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </div>
        )}

        {/* Success State */}
        {submitted && (
          <div className="max-w-lg mx-auto text-center py-12 animate-fade-in">
            <div className="w-16 h-16 rounded-full bg-[#7EA13E]/15 flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-[#7EA13E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-[#2C2A2B] mb-2">Thank You!</h3>
            <p className="text-[#6B6567]">We've received your enquiry and will get back to you shortly.</p>
          </div>
        )}
      </div>
    </section>
  );
};
